// Persist the date-range filter chosen on a page so it survives reloads
// and re-visits.
//
// Two shapes are stored, so that relative presets stay relative:
//   - "preset": only the preset id is stored (e.g. "last_7_days"). The range is
//     re-computed against the current date every time the user visits the page.
//   - "custom": the literal from/to dates are stored (YYYY-MM-DD).
//
// Keys are scoped per Keycloak user id so that multiple accounts on the same
// browser don't leak filter state across each other.

import dayjs from "dayjs";
import { PRESET_COMPUTE } from "@/helpers/datePresets";
import KeycloakSession from "@/helpers/keycloak-session";

export type DateFilterPageKey = "jobs" | "tasks" | "appointments" | "payments" | "offers";

export type SavedDateFilter =
  | { kind: "preset"; id: string; v: 1 }
  | { kind: "custom"; from: string; to: string; v: 1 };

const STORAGE_PREFIX = "gastools.filter.date";

function getUserId(): string {
  try {
    const kc = (window as any).sharedKeycloak;
    const sub = kc?.tokenParsed?.sub;
    return typeof sub === "string" && sub.length > 0 ? sub : "anon";
  } catch {
    return "anon";
  }
}

function storageKey(pageKey: DateFilterPageKey): string {
  return `${STORAGE_PREFIX}.${getUserId()}.${pageKey}`;
}

export function isPaymentEditor(): boolean {
  const kc = new KeycloakSession();
  return kc.userHasRole("editor") && !kc.userHasRole("admin");
}

export function clampPaymentDateRangeForEditor(
  from: string,
  _to: string
): { from: string; to: string } {
  const day = dayjs(from).startOf("day");
  return {
    from: day.startOf("day").toISOString(),
    to: day.endOf("day").toISOString(),
  };
}

export function saveDateFilter(
  pageKey: DateFilterPageKey,
  value: SavedDateFilter
): void {
  try {
    localStorage.setItem(storageKey(pageKey), JSON.stringify(value));
  } catch {
    // localStorage disabled/full — feature is best-effort, fall through.
  }
}

export function clearDateFilter(pageKey: DateFilterPageKey): void {
  try {
    localStorage.removeItem(storageKey(pageKey));
  } catch {
    // no-op
  }
}

export function loadDateFilter(
  pageKey: DateFilterPageKey
): SavedDateFilter | null {
  try {
    const raw = localStorage.getItem(storageKey(pageKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== 1) return null;
    if (parsed.kind === "preset" && typeof parsed.id === "string") {
      return parsed as SavedDateFilter;
    }
    if (
      parsed.kind === "custom" &&
      typeof parsed.from === "string" &&
      typeof parsed.to === "string"
    ) {
      return parsed as SavedDateFilter;
    }
    return null;
  } catch {
    return null;
  }
}

export interface ResolvedDateRange {
  // ISO strings aligned to startOf('day') / endOf('day') — matches what every
  // filter subcomponent already builds by hand for the API layer.
  from: string;
  to: string;
  // Present when the resolved range came from a relative preset.
  presetId?: string;
}

// Returns the stored range re-computed against "today" (for presets) or read
// verbatim (for custom). Returns null when nothing is stored or the stored
// preset is unknown — callers should fall back to their own default.
export function resolveInitialDateRange(
  pageKey: DateFilterPageKey
): ResolvedDateRange | null {
  const saved = loadDateFilter(pageKey);
  if (!saved) return null;

  if (saved.kind === "preset") {
    const compute = PRESET_COMPUTE[saved.id];
    if (!compute) {
      // Unknown preset id (e.g. we removed it in a later version). Discard so
      // a stale entry doesn't haunt the user forever.
      clearDateFilter(pageKey);
      return null;
    }
    const [fromYMD, toYMD] = compute();
    return {
      from: dayjs(fromYMD).startOf("day").toISOString(),
      to: dayjs(toYMD).endOf("day").toISOString(),
      presetId: saved.id,
    };
  }

  return {
    from: dayjs(saved.from).startOf("day").toISOString(),
    to: dayjs(saved.to).endOf("day").toISOString(),
  };
}

// Payments page initial range — editors may only query a single day.
export function resolveInitialPaymentDateRange(): {
  from: string;
  to: string;
} {
  const resolved = resolveInitialDateRange("payments");
  const from = resolved?.from ?? dayjs().startOf("day").toISOString();
  const to = resolved?.to ?? dayjs().endOf("day").toISOString();
  if (isPaymentEditor()) {
    return clampPaymentDateRangeForEditor(from, to);
  }
  return { from, to };
}
